import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { CarouselProps } from './carousel.types'
import { Icon } from '../icon'
import { handleScroll, handleSlideChange } from './carousel.utils'

export const CarouselSlidesContainer = styled.div`
  ${({ theme }) => `
    display: flex;
    padding: ${theme.sizes.s};
    margin: -${theme.sizes.s};
    overflow: auto hidden;
    gap: 32px;
    scrollbar-width: none;
    scroll-behavior: smooth;
    scroll-snap-stop: always;
    scroll-snap-type: x mandatory;

    &::-webkit-scrollbar {
      display: none;
    }
  `}
`

export const CarouselSlide = styled.div`
  ${() => `
    flex-shrink: 0;
    width: 100%;
    height: 100%;
    scroll-snap-align: center;
  `}
`

export const NavbarWrapper = styled.div`
  ${({ theme }) => `
    margin-bottom: ${theme.sizes.s};
    display: flex;
    align-items: center;
    justify-content: space-between;
  `}
`

export const Title = styled.div`
  ${({ theme }) => {
    return `
    color: ${theme.colors.black};
    font-size: ${theme.fontSizes.h2};
    font-weight: ${theme.fontWeights.semiBold};
    line-height: ${theme.sizes[8]};

    @media (max-width: 767px) {
      font-size: ${theme.fontSizes.h3};
      line-height: ${theme.sizes[6]};
    }
  `
  }}
`

export const NavArrowsWrapper = styled.div`
  ${({ theme }) => `
    display: flex;
    gap: ${theme.sizes.xs}
  `}
`

export const NavArrow = styled.button`
  ${({ theme }) => `
    color: ${theme.colors.gray900};
    border-color: ${theme.colors.gray100};
    border-width: 1px;
    border-style: solid;
    border-radius: 50%;
    background: none;
    width: ${theme.sizes.l};
    min-width: ${theme.sizes.l};
    height: ${theme.sizes.l};
    display: flex;
    flex-direction: column;
    justify-content: center;
    &:hover {
      color: ${theme.colors.primary};
    };
    &:disabled {
      color: ${theme.colors.gray100};
      border-color: ${theme.colors.gray100};
    };
  `}
`

export function Carousel({ items, title, ...props }: CarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState<number>(0)

  const changeSlide = (index: number) => {
    setCurrentIndex(handleSlideChange(index, items.length, containerRef))
  }

  useEffect(() => {
    const container = containerRef.current
    const onScroll = () => handleScroll(containerRef, setCurrentIndex)

    if (container) {
      container.addEventListener('scroll', onScroll)
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', onScroll)
      }
    }
  }, [])

  return (
    <div {...props}>
      {items?.length === 0 ? null : (
        <>
          <NavbarWrapper>
            <Title>{title}</Title>
            {items.length > 1 && (
              <NavArrowsWrapper role="navigation">
                <NavArrow onClick={() => changeSlide(currentIndex - 1)}>
                  <Icon name="arrow-left" size={5} />
                </NavArrow>
                <NavArrow onClick={() => changeSlide(currentIndex + 1)}>
                  <Icon name="arrow-right" size={5} />
                </NavArrow>
              </NavArrowsWrapper>
            )}
          </NavbarWrapper>
          <CarouselSlidesContainer ref={containerRef}>
            {items.map((item, index) => (
              <CarouselSlide key={index}>{item}</CarouselSlide>
            ))}
          </CarouselSlidesContainer>
        </>
      )}
    </div>
  )
}
